/**
 * Aggressive localStorage cleanup utility
 */

export function getStorageInfo() {
  const items: { [key: string]: number } = {};
  let totalSize = 0;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const size = (localStorage.getItem(key) || '').length;
      items[key] = size;
      totalSize += size + key.length;
    }
  }
  
  // Sort by size
  const sorted = Object.entries(items)
    .sort(([, a], [, b]) => b - a)
    .map(([key, size]) => ({ key, size, sizeKB: (size / 1024).toFixed(2) }));
  
  return {
    totalSizeKB: (totalSize / 1024).toFixed(2),
    itemCount: localStorage.length,
    items: sorted,
    largestItems: sorted.slice(0, 10)
  };
}

export function aggressiveCacheClear() {
  const keysToRemove: string[] = [];
  
  // Remove ALL cache items
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith('cache_') || 
      key.startsWith('insightatlas-') ||
      key.includes('research') ||
      key.includes('topic') ||
      key.includes('saved')
    )) {
      keysToRemove.push(key);
    }
  }
  
  // Remove them all
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Failed to remove ${key}:`, e);
    }
  });
  
  console.log(`🧹 Removed ${keysToRemove.length} cache items`);
  return keysToRemove.length;
}

export function clearNonEssentialData() {
  const essentialKeys = [
    'access_token',
    'auth',
    'insightatlas-theme',
    'google_auth'
  ];
  
  const keysToRemove: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !essentialKeys.some(essential => key.includes(essential))) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Failed to remove ${key}:`, e);
    }
  });
  
  console.log(`🧹 Cleared ${keysToRemove.length} non-essential items`);
  return keysToRemove.length;
}

// Disable localStorage caching temporarily
export function disableLocalStorageCaching() {
  // Override localStorage.setItem to skip cache writes
  const originalSetItem = localStorage.setItem;
  localStorage.setItem = function(key: string, value: string) {
    // Only allow essential keys
    if (key === 'access_token' || key === 'insightatlas-theme' || key === 'auth') {
      return originalSetItem.call(this, key, value);
    }
    // Skip cache writes silently
    if (key.startsWith('cache_')) {
      console.log(`[Cache] Skipping localStorage write for ${key} (storage full)`);
      return;
    }
    return originalSetItem.call(this, key, value);
  };
}

// Make functions available globally
if (typeof window !== 'undefined') {
  (window as any).getStorageInfo = getStorageInfo;
  (window as any).aggressiveCacheClear = aggressiveCacheClear;
  (window as any).clearNonEssentialData = clearNonEssentialData;
  (window as any).disableLocalStorageCaching = disableLocalStorageCaching;
}
